
import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

import "./RichTextEditor.css";

export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
    ],
    content: value || "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        "aria-label": "Journal article content",
        class: "rich-text-content",
      },
    },
  });

  // Load saved content when opening or editing an article.
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      const nextContent = value || "";

      if (currentContent !== nextContent) {
        editor.commands.setContent(nextContent, {
          emitUpdate: false,
        });
      }
    }
  }, [editor, value]);

  if (!editor) return null;

  const addLink = () => {
    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter the link URL:", previousUrl);

    if (url === null) return;

    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    try {
      const parsedUrl = new URL(url.trim());

      if (!["http:", "https:", "mailto:"].includes(parsedUrl.protocol)) {
        window.alert("Please enter a valid web or email link.");
        return;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: parsedUrl.href })
        .run();
    } catch {
      window.alert("Please enter a valid URL, such as https://example.com.");
    }
  };

  const tools = [
    {
      label: "B",
      title: "Bold",
      action: () => editor.chain().focus().toggleBold().run(),
      active: editor.isActive("bold"),
    },
    {
      label: "I",
      title: "Italic",
      action: () => editor.chain().focus().toggleItalic().run(),
      active: editor.isActive("italic"),
    },
    {
      label: "U",
      title: "Underline",
      action: () => editor.chain().focus().toggleUnderline().run(),
      active: editor.isActive("underline"),
    },
    {
      label: "H2",
      title: "Heading 2",
      action: () =>
        editor.chain().focus().toggleHeading({ level: 2 }).run(),
      active: editor.isActive("heading", { level: 2 }),
    },
    {
      label: "H3",
      title: "Heading 3",
      action: () =>
        editor.chain().focus().toggleHeading({ level: 3 }).run(),
      active: editor.isActive("heading", { level: 3 }),
    },
    {
      label: "• List",
      title: "Bullet list",
      action: () => editor.chain().focus().toggleBulletList().run(),
      active: editor.isActive("bulletList"),
    },
    {
      label: "1. List",
      title: "Numbered list",
      action: () => editor.chain().focus().toggleOrderedList().run(),
      active: editor.isActive("orderedList"),
    },
    {
      label: "❝",
      title: "Blockquote",
      action: () => editor.chain().focus().toggleBlockquote().run(),
      active: editor.isActive("blockquote"),
    },
  ];

  return (
    <div className="rich-text-editor">
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        {tools.map((tool) => (
          <button
            key={tool.title}
            type="button"
            title={tool.title}
            aria-label={tool.title}
            aria-pressed={tool.active}
            className={tool.active ? "active" : ""}
            onMouseDown={(event) => event.preventDefault()}
            onClick={tool.action}
          >
            {tool.label}
          </button>
        ))}

        <button
          type="button"
          title="Add link"
          aria-label="Add link"
          className={editor.isActive("link") ? "active" : ""}
          onMouseDown={(event) => event.preventDefault()}
          onClick={addLink}
        >
          Link
        </button>

        <button
          type="button"
          title="Clear formatting"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          Clear
        </button>
      </div>

      <EditorContent editor={editor} />

      <div className="rich-text-footer">
        Use headings and lists to make your article easier to read.
      </div>
    </div>
  );
}
